import type {
  PortalAppointment,
  PortalCase,
  PortalReminder,
  PortalTask,
  PortalUpdate
} from "./types";

/** The slice of i18next's `t` the model needs, so tests can pass a stub. */
export type Translate = (
  key: string,
  options?: Record<string, unknown>
) => string;

const DAY = 24 * 60 * 60 * 1000;

/** "2026-10-20" as a local calendar day (no timezone drift); full ISO as-is. */
export const parseDay = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = day
    ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]))
    : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate());

export const daysBetween = (from: Date, to: Date): number =>
  Math.round((startOfDay(to).getTime() - startOfDay(from).getTime()) / DAY);

export const formatDay = (
  value: string | null | undefined,
  language: string,
  now: Date = new Date()
): string => {
  const date = parseDay(value);
  if (!date) return "";
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(language, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" })
  }).format(date);
};

export const greetingKey = (hour: number): string => {
  if (hour < 12) return "greeting.morning";
  if (hour < 18) return "greeting.afternoon";
  return "greeting.evening";
};

export const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

export type TaskAction =
  | "start"
  | "continue"
  | "update"
  | "sign"
  | "ink"
  | "notYet";

/** Signing the firm hasn't opened yet is listed so the client knows it's coming. */
export const isTaskReady = (task: PortalTask): boolean => task.ready !== false;

export const taskAction = (task: PortalTask): TaskAction => {
  if (!isTaskReady(task)) return "notYet";
  if (task.kind === "sign") return "sign";
  if (task.kind === "ink") return "ink";
  if (task.changes_requested) return "update";
  return task.answered > 0 ? "continue" : "start";
};

export const taskPercent = (task: PortalTask): number =>
  task.total > 0 ? Math.round((task.answered / task.total) * 100) : 0;

/** Ready before not-yet; then changes requested, soonest due, latest touched. */
export const sortTasks = (tasks: PortalTask[]): PortalTask[] =>
  [...tasks].sort((a, b) => {
    if (isTaskReady(a) !== isTaskReady(b)) return isTaskReady(a) ? -1 : 1;
    if (a.changes_requested !== b.changes_requested)
      return a.changes_requested ? -1 : 1;
    const ad = a.due ?? "9999";
    const bd = b.due ?? "9999";
    if (ad !== bd) return ad < bd ? -1 : 1;
    return (b.updated_at ?? "").localeCompare(a.updated_at ?? "");
  });

/** "3 of 12 answered · about 4 minutes left · due Oct 20". */
export const taskMeta = (
  task: PortalTask,
  t: Translate,
  language: string,
  now: Date = new Date()
): string => {
  if (task.kind !== "intake") {
    if (!isTaskReady(task)) return t("tasks.upcoming");
    return t(task.kind === "sign" ? "tasks.signMeta" : "tasks.inkMeta", {
      minutes: task.minutes_left
    });
  }
  const parts: string[] = [];
  if (task.total > 0)
    parts.push(
      t("tasks.progress", { answered: task.answered, total: task.total })
    );
  if (task.minutes_left > 0)
    parts.push(t("tasks.minutesLeft", { count: task.minutes_left }));
  const due = parseDay(task.due);
  if (due) {
    const days = daysBetween(now, due);
    const date = formatDay(task.due, language, now);
    if (days === 0) parts.push(t("tasks.dueToday"));
    else if (days < 0) parts.push(t("tasks.overdue", { date }));
    else parts.push(t("tasks.due", { date }));
  }
  return parts.join(" · ");
};

export const isOverdue = (
  task: PortalTask,
  now: Date = new Date()
): boolean => {
  const due = parseDay(task.due);
  return Boolean(due && daysBetween(now, due) < 0);
};

const REMINDER_KINDS = new Set([
  "immigration_status_expiration",
  "i94_expiration",
  "passport_expiration",
  "ead_expiration",
  "advance_parole_expiration",
  "visa_expiration"
]);

/** "your passport", "Ana's visa"; the server's English for unknown kinds. */
export const reminderSubject = (
  reminder: PortalReminder,
  t: Translate
): string => {
  const { kind, dependent, status } = reminder;
  if (!REMINDER_KINDS.has(kind)) return reminder.subject;
  if (dependent) return t(`reminders.theirs.${kind}`, { name: dependent });
  if (kind === "immigration_status_expiration" && status)
    return t("reminders.yours.status", { status });
  return t(`reminders.yours.${kind}`);
};

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export const reminderLine = (
  reminder: PortalReminder,
  t: Translate,
  language: string
): string => {
  const subject = reminderSubject(reminder, t);
  if (reminder.expired)
    return capitalize(
      t("reminders.expired", {
        subject,
        date: formatDay(reminder.due, language)
      })
    );
  if (reminder.days_left <= 0)
    return capitalize(t("reminders.expiresToday", { subject }));
  return capitalize(
    t("reminders.expiresIn", { subject, count: reminder.days_left })
  );
};

/** The firm's own words win; otherwise the stage's explainer, translated. */
export const explainerText = (c: PortalCase, t: Translate): string => {
  if (c.explainer_custom) return c.explainer;
  const lead = c.lead ?? t("explainer.yourTeam");
  const leadFirst = c.lead
    ? c.lead.split(" ")[0]
    : t("explainer.yourTeamLower");
  if (c.milestone) return t(`milestone.${c.milestone}`, { lead, leadFirst });
  return t(`explainer.${c.stage}`, { lead, leadFirst });
};

export const updateLine = (update: PortalUpdate, t: Translate): string => {
  const key = `updates.${update.kind}`;
  const line = t(key, update.params);
  return line === key ? update.text : line;
};

/** "today", "yesterday", "3 days ago", then a date. */
export const relativeDay = (
  value: string | null | undefined,
  t: Translate,
  language: string,
  now: Date = new Date()
): string => {
  const date = parseDay(value);
  if (!date) return "";
  const days = daysBetween(date, now);
  if (days <= 0) return t("common.today");
  if (days === 1) return t("common.yesterday");
  if (days < 7) return t("common.daysAgo", { count: days });
  return formatDay(value, language, now);
};

export type ComingUpItem = {
  key: string;
  kind: "appointment" | "task";
  title: string;
  when: string;
  at: string;
};

const formatTime = (iso: string, language: string) =>
  new Intl.DateTimeFormat(language, {
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(iso));

const weekdayDay = (value: string, language: string) => {
  const date = parseDay(value);
  return date
    ? new Intl.DateTimeFormat(language, {
        weekday: "short",
        month: "short",
        day: "numeric"
      }).format(date)
    : "";
};

/** Shared appointments and task due dates, soonest first. */
export const comingUp = (
  appointments: PortalAppointment[],
  tasks: PortalTask[],
  t: Translate,
  language: string,
  now: Date = new Date()
): ComingUpItem[] => {
  const items: ComingUpItem[] = appointments.map((a) => ({
    key: `a-${a.id}`,
    kind: "appointment",
    title: a.title,
    when: [
      weekdayDay(a.start, language),
      a.all_day ? t("comingUp.allDay") : formatTime(a.start, language),
      a.location
    ]
      .filter(Boolean)
      .join(" · "),
    at: a.start
  }));
  tasks.forEach((task) => {
    const due = parseDay(task.due);
    if (!due || daysBetween(now, due) < 0) return;
    items.push({
      key: `t-${task.id}`,
      kind: "task",
      title: t("comingUp.taskDue", { title: task.title }),
      when: weekdayDay(task.due ?? "", language),
      at: due.toISOString()
    });
  });
  return items.sort((a, b) => a.at.localeCompare(b.at));
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
